import { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { SegmentedControl } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { useMount } from "app/custom-hooks/useMount";
import { translate } from "app/filters/i18nFilter";
import { ColumnPickers } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/ColumnPickers";
import ContactsColumnPickerHead from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/ContactsColumnPickerHead";
import { ContactsEnrichmentSummary } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/ContactsEnrichmentSummary";
import { Footer } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/Footer";
import { Header } from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/Header";
import InfoTooltip from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/InfoTooltip";
import QuotaTooltip from "app/pages/sales-intelligence/modules/enrichment/components/enrichment-csv/QuotaTooltip";
import { HeadersDropdown } from "app/pages/sales-intelligence/modules/enrichment/components/HeadersDropdown/HeadersDropdown";
import { useContactsColumnConfigs } from "app/pages/sales-intelligence/modules/enrichment/hooks/useContactsColumnConfigs";
import { useCsvEnrichmentQuota } from "app/pages/sales-intelligence/modules/enrichment/hooks/useCsvEnrichmentQuota";
import { useSelectedConfigFields } from "app/pages/sales-intelligence/modules/enrichment/hooks/useSelectedConfigFields";
import { getContactsSelectedColumnsConfigThunk } from "app/pages/sales-intelligence/modules/enrichment/store/effects/contactsEnrichment";
import {
    selectContactsParsed,
    selectContactsSelectedColumnsConfig,
    selectIsEnriching,
} from "app/pages/sales-intelligence/modules/enrichment/store/selectors/selectors";
import type { PostContactsCsvProps } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import type {
    ConfigField,
    CsvExportColumnCategory,
} from "app/pages/sales-intelligence/modules/enrichment/types/columns";
import { createFieldBadgeComponent } from "app/pages/sales-intelligence/modules/enrichment/utils/componentFactories";
import {
    COMPANY_HEADER_TEXT,
    CONTACTS_MODAL_TITLE,
    DATA_CREDITS_CONTACTS_TOOLTIP,
    DISABLED_FULLNAME_TOOLTIP_CONTENT,
    DISABLED_LINKEDIN_TOOLTIP_CONTENT,
    FIRST_NAME_HEADER_TEXT,
    FULLNAME_HEADER_TEXT,
    LAST_NAME_HEADER_TEXT,
    LINKEDIN_HEADER_TEXT,
    TOTAL_CONTACTS,
    TOTAL_CONTACTS_SHORT,
    TOTAL_CONTACTS_TOOLTIP,
} from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/modal.texts";
import { sortConfigFieldsByName } from "app/pages/sales-intelligence/modules/enrichment/utils/utils";
import QuotaTotalCost from "app/pages/sales-intelligence/modules/quota/components/QuotaTotalCost";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import { selectContactsCost } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type { ContactsHeaderSelection } from "app/pages/sales-intelligence/sub-modules/management/components/types";

import {
    StyledDomainDropdownWrapper,
    StyledDownloadEnrichedContainer,
    StyledDropdownSection,
    StyledDropdownWrapper,
    StyledExampleDomains,
    StyledHelpText,
    StyledModalBody,
} from "./style";

type DownloadEnrichedProps = {
    onConfirm: (props: PostContactsCsvProps) => void;
    onReplace: VoidFunction;
    onClose: VoidFunction;
    setSelectedHeaders: (value: ContactsHeaderSelection) => void;
    selectedHeaders: ContactsHeaderSelection;
    fileName: string;
};

enum NameVariant {
    FirstAndLastName = "First & last name",
    Fullname = "Full name",
}

export const EnrichmentContactsCsvContainer = ({
    onReplace,
    onClose,
    onConfirm,
    setSelectedHeaders,
    selectedHeaders,
    fileName,
}: DownloadEnrichedProps) => {
    const parsingResult = useSelector(selectContactsParsed);
    const isEnriching = useSelector(selectIsEnriching);
    const costPerRecord = useSelector(selectContactsCost);
    const selectedColumnConfigFields = useSelector(selectContactsSelectedColumnsConfig);
    const dispatch = useDispatch();

    const [selectErrors, setSelectErrors] = useState<Partial<Record<keyof ContactsHeaderSelection, boolean>>>({});

    const [isScrollTracked, setIsScrollTracked] = useState(false);
    const modalBodyRef = useRef<HTMLDivElement>(null);

    const { headerNames, linkedinHeaderNames, fullnameHeaderNames, fileParsed, totalRowCount } = parsingResult;

    const { config, isLoading: isConfigLoading } = useContactsColumnConfigs();
    const { selectedConfigFields, setSelectedConfigByName, setAllConfigs } = useSelectedConfigFields(config, "contact");

    useMount(() => {
        void dispatch(getContactsSelectedColumnsConfigThunk());
    });

    useEffect(() => {
        if (selectedColumnConfigFields?.length > 0 && config?.length > 0) {
            setAllConfigs(sortConfigFieldsByName({ defaultFields: selectedColumnConfigFields, columnsConfig: config }));
        }
    }, [selectedColumnConfigFields, config]);

    const allSelectedFields = Object.values(selectedConfigFields).flat();

    const { isDataCreditsEnabled, isOutOfQuota, quota, totalCost, getFieldCost } = useCsvEnrichmentQuota({
        selectedFields: selectedConfigFields["companyAndWebsite"] ?? [],
        matchedRecordsCount: totalRowCount,
        basicPrice: costPerRecord,
    });
    const renderFieldBadge = createFieldBadgeComponent({ isDataCreditsEnabled, getFieldCost });

    const onConfigApply = (configName: CsvExportColumnCategory, selectedFields: ConfigField[]) => {
        setSelectedConfigByName(configName, selectedFields);
    };

    const isLinkedinVisible = linkedinHeaderNames.length > 0;
    const isFullnameVisible = fullnameHeaderNames.length > 0;
    const headerNamesLength = headerNames.length;

    const headersSelectVisibility = {
        linkedinUrl: isLinkedinVisible,
        domain: headerNamesLength > (isLinkedinVisible ? (isFullnameVisible ? 2 : 3) : isFullnameVisible ? 1 : 2),
        fullName: isFullnameVisible && headerNamesLength > (isLinkedinVisible ? 2 : 1),
        firstName: headerNamesLength > (isLinkedinVisible ? 3 : 2),
        lastName: headerNamesLength > (isLinkedinVisible ? 3 : 2),
    };

    const [nameVariant, setNameVariant] = useState<NameVariant>(() =>
        headersSelectVisibility.fullName ? NameVariant.Fullname : NameVariant.FirstAndLastName,
    );

    useEffect(() => {
        if (headersSelectVisibility.fullName) {
            setNameVariant(NameVariant.Fullname);
        }
    }, []);

    const isHeaderSelectionValid = () => {
        if (headersSelectVisibility.linkedinUrl) {
            const isValid = !!selectedHeaders.linkedinUrl;
            if (!isValid) {
                setSelectErrors((prev) => ({ ...prev, linkedinUrl: true }));
            }

            return !!selectedHeaders.linkedinUrl;
        }

        const isDomainValid = !!selectedHeaders.domain;
        const isFullNameValid = !!selectedHeaders.fullName;
        const isFirstNameValid = !!selectedHeaders.firstName;
        const isLastNameValid = !!selectedHeaders.lastName;

        if (nameVariant === NameVariant.FirstAndLastName) {
            if (!isFirstNameValid) {
                setSelectErrors((prev) => ({ ...prev, firstName: true }));
            }
            if (!isLastNameValid) {
                setSelectErrors((prev) => ({ ...prev, lastName: true }));
            }
        } else {
            if (!isFullNameValid) {
                setSelectErrors((prev) => ({ ...prev, fullName: true }));
            }
        }

        if (!isDomainValid) {
            setSelectErrors((prev) => ({ ...prev, domain: true }));
            return false;
        }

        return isFullNameValid || (isFirstNameValid && isLastNameValid);
    };

    const handleApply = () => {
        if (!isHeaderSelectionValid()) {
            return;
        }

        setSelectedHeaders({
            linkedinUrl: selectedHeaders.linkedinUrl,
            domain: selectedHeaders.domain,
            ...(nameVariant === NameVariant.Fullname
                ? { fullName: selectedHeaders.fullName }
                : { firstName: selectedHeaders.firstName, lastName: selectedHeaders.lastName }),
        });

        onConfirm({
            columns: selectedConfigFields,
            allSelectedColumnsLength: allSelectedFields.length,
            parsingResult: {
                fileParsed,
                headerNames,
                totalRowCount,
            },
        });
    };

    useEffect(() => {
        const scrollHandler = () => {
            if (!isScrollTracked) {
                enrichmentTrackingService.csv.contacts.trackModalScroll();
                setIsScrollTracked(true);
            }
        };
        modalBodyRef?.current?.addEventListener("scroll", scrollHandler, { passive: true });
        return () => {
            modalBodyRef?.current?.removeEventListener("scroll", scrollHandler);
        };
    }, [isScrollTracked]);

    const isDownloadDisabled = !(!!selectedHeaders && !!allSelectedFields.length);

    const renderDropdownSection = (
        title: string,
        key: keyof ContactsHeaderSelection,
        getIsOptionDisabled?: (value: string) => boolean,
        disabledTooltipText?: string,
    ) => {
        return (
            <StyledDropdownWrapper marginBottom="16px">
                <StyledHelpText>{title}</StyledHelpText>
                <StyledDomainDropdownWrapper className={`headers-${key}-dropdown-container`}>
                    <HeadersDropdown
                        headerNames={headerNames}
                        selectedHeader={selectedHeaders[key] ?? undefined}
                        setSelectedHeader={(headerName) => {
                            setSelectedHeaders({ ...selectedHeaders, [key]: headerName });
                        }}
                        appendTo={`.headers-${key}-dropdown-container`}
                        getIsOptionDisabled={getIsOptionDisabled}
                        disabledTooltipText={disabledTooltipText}
                        isError={!!selectErrors[key]}
                        eventName="contact column"
                        type="contact"
                        onToggle={() => {
                            setSelectErrors((prev) => ({ ...prev, [key]: false }));
                        }}
                    />
                    {selectedHeaders[key] ? (
                        <Flex
                            alignItems="center"
                            overflow="hidden"
                            width="100%"
                        >
                            <StyledExampleDomains>
                                {fileParsed[selectedHeaders[key]].filter(Boolean).slice(0, 2).join(", ")}
                            </StyledExampleDomains>
                            {fileParsed[selectedHeaders[key]].length > 2 ? (
                                <Text
                                    fontVariant="body1"
                                    color={colorsPalettes.carbon[200]}
                                    margin="0 0 0 4px"
                                >
                                    {`+${fileParsed[selectedHeaders[key]].length - (key === "linkedinUrl" ? 1 : 2)}`}
                                </Text>
                            ) : null}
                        </Flex>
                    ) : null}
                </StyledDomainDropdownWrapper>
            </StyledDropdownWrapper>
        );
    };

    const renderHeaderSelects = () => {
        return (
            <>
                {headersSelectVisibility.linkedinUrl
                    ? renderDropdownSection(
                          translate(LINKEDIN_HEADER_TEXT),
                          "linkedinUrl",
                          (header) => !linkedinHeaderNames.includes(header),
                          DISABLED_LINKEDIN_TOOLTIP_CONTENT,
                      )
                    : null}
                {headersSelectVisibility.domain
                    ? renderDropdownSection(translate(COMPANY_HEADER_TEXT), "domain")
                    : null}
                {headersSelectVisibility.fullName &&
                headersSelectVisibility.firstName &&
                headersSelectVisibility.lastName ? (
                    <Box margin="0 0 16px 0">
                        <SegmentedControl
                            values={Object.values(NameVariant)}
                            onChange={(index: number) => {
                                setNameVariant(Object.values(NameVariant)[index]);
                            }}
                            defaultSelectedIndex={Object.values(NameVariant).indexOf(nameVariant)}
                        />
                    </Box>
                ) : null}
                {nameVariant === NameVariant.Fullname ? (
                    <>
                        {headersSelectVisibility.fullName
                            ? renderDropdownSection(
                                  translate(FULLNAME_HEADER_TEXT),
                                  "fullName",
                                  (header) => !fullnameHeaderNames.includes(header),
                                  DISABLED_FULLNAME_TOOLTIP_CONTENT,
                              )
                            : null}
                    </>
                ) : (
                    <>
                        {headersSelectVisibility.firstName
                            ? renderDropdownSection(translate(FIRST_NAME_HEADER_TEXT), "firstName")
                            : null}
                        {headersSelectVisibility.lastName
                            ? renderDropdownSection(translate(LAST_NAME_HEADER_TEXT), "lastName")
                            : null}
                    </>
                )}
            </>
        );
    };

    return (
        <StyledDownloadEnrichedContainer>
            <Header
                iconSize="xl"
                iconName="refresh_list"
                className="replaceWrapper"
                title={fileName}
                onClick={onReplace}
            />
            <StyledModalBody
                type="contact"
                ref={modalBodyRef}
            >
                <Text
                    fontVariant="subtitle1"
                    margin="0 0 16px"
                    display="block"
                >
                    {translate(CONTACTS_MODAL_TITLE)}
                </Text>
                <StyledDropdownSection withBorder>{renderHeaderSelects()}</StyledDropdownSection>

                <ColumnPickers
                    isLoading={isConfigLoading}
                    config={config}
                    selectedConfigFields={selectedConfigFields}
                    onApply={onConfigApply}
                    type="contact"
                    head={<ContactsColumnPickerHead />}
                    renderFieldBadge={renderFieldBadge}
                />
            </StyledModalBody>

            <Footer
                isDisabled={isDownloadDisabled || isEnriching}
                isOutOfQuota={isOutOfQuota}
                onClose={onClose}
                onConfirm={handleApply}
            >
                <ContactsEnrichmentSummary
                    rowsCount={totalRowCount}
                    summaryText={translate(isDataCreditsEnabled ? TOTAL_CONTACTS_SHORT : TOTAL_CONTACTS, {
                        total: totalRowCount,
                    })}
                    infoTooltip={
                        <InfoTooltip
                            defaultText={
                                isDataCreditsEnabled
                                    ? translate(DATA_CREDITS_CONTACTS_TOOLTIP, { costPerRecord })
                                    : translate(TOTAL_CONTACTS_TOOLTIP)
                            }
                            quota={quota.remaining}
                            withLegacyQuotaView={!isDataCreditsEnabled ? isOutOfQuota : false}
                        />
                    }
                    badge={
                        isDataCreditsEnabled ? (
                            <QuotaTooltip
                                isOutOfQuota={isOutOfQuota}
                                quota={quota.remaining}
                            >
                                <QuotaTotalCost
                                    isOutOfQuota={isOutOfQuota}
                                    totalCost={totalCost}
                                />
                            </QuotaTooltip>
                        ) : null
                    }
                    isOutOfQuota={isOutOfQuota}
                />
            </Footer>
        </StyledDownloadEnrichedContainer>
    );
};
